import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AdminDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const ADMIN_EMAIL = 'anbunathan.r@gmail.com';

interface UserRecord {
  email: string;
  fullName: string;
  mobile: string;
  role: string;
  tenantId: string;
  plan: string;
  status: string;
  createdAt: string;
  lastLoginAt: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) return;
    fetchUsers();
  }, [isAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (data.success) setUsers(data.data || []);
      else setError(data.error || 'Failed to load users');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const changePlan = async (email: string, newPlan: string) => {
    setUpdating(email);
    try {
      const res = await fetch(`${API_BASE}/admin/users/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ email, plan: newPlan }),
      });
      const data = await res.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u.email === email ? { ...u, plan: newPlan } : u));
      } else {
        setError(data.error || 'Failed to update plan');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUpdating(null);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2>🚫 Access Denied</h2>
        <p>Only admin can access this page.</p>
        <button className="btn-primary" onClick={() => navigate('/app')}>← Back to Dashboard</button>
      </div>
    );
  }

  const filtered = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.fullName.toLowerCase().includes(search.toLowerCase())
  );

  const totalFree = users.filter(u => (u.plan || 'free') === 'free').length;
  const totalPro = users.filter(u => u.plan === 'subscriber').length;

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>👑 Admin Dashboard</h1>
        <div className="admin-stats">
          <span className="admin-stat"><strong>{users.length}</strong> Total Users</span>
          <span className="admin-stat free"><strong>{totalFree}</strong> Free</span>
          <span className="admin-stat pro"><strong>{totalPro}</strong> Pro</span>
        </div>
      </div>

      {error && <div className="admin-error">⚠️ {error} <button onClick={() => setError('')}>✕</button></div>}

      <div className="admin-toolbar">
        <input
          type="text"
          placeholder="🔍 Search by email or name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="admin-search"
        />
        <button className="btn-secondary" onClick={fetchUsers} disabled={loading}>
          🔄 Refresh
        </button>
      </div>

      {loading ? (
        <div className="admin-loading">⏳ Loading users...</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, idx) => {
                const currentPlan = u.plan || 'free';
                return (
                  <tr key={u.email}>
                    <td>{idx + 1}</td>
                    <td><strong>{u.fullName}</strong></td>
                    <td className="email-cell">{u.email}</td>
                    <td>{u.mobile || '—'}</td>
                    <td>
                      <span className={`plan-badge-${currentPlan}`}>
                        {currentPlan === 'subscriber' ? '⭐ Pro' : '🆓 Free'}
                      </span>
                    </td>
                    <td><span className="status-active">● Active</span></td>
                    <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                    <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}</td>
                    <td className="action-cell">
                      {currentPlan === 'free' ? (
                        <button
                          className="btn-upgrade-sm"
                          onClick={() => changePlan(u.email, 'subscriber')}
                          disabled={updating === u.email}
                        >
                          {updating === u.email ? '⏳' : '⬆️ Upgrade to Pro'}
                        </button>
                      ) : (
                        <button
                          className="btn-downgrade-sm"
                          onClick={() => changePlan(u.email, 'free')}
                          disabled={updating === u.email}
                        >
                          {updating === u.email ? '⏳' : '⬇️ Downgrade to Free'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: '#7a8899' }}>No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
